import { forwardRef } from 'react';
import { Image, compressedImageUrl } from '../design-system';
import {
  CARD_BOX_ASPECT,
  CARD_CORNER_RADIUS,
  CLEAR_TRANSITION_MS,
  REVEAL_FADE_MS,
  type AnimMode,
} from './readyStep.constants';
import { useImageAspect, containedRectStyle } from './containedRect';
import { useIsSleek } from '../theme';
import { cardHeight } from './stageLayout';

// The reel tile's <Image> CDN params. Exported so useNextPackItems preloads
// the EXACT same resized URL the tiles request (warm cache, no re-download).
// quality:75 — tiles spin and blur; saving ~2-3x bandwidth at no visible cost.
export const REEL_IMG = { width: 232, height: 367, quality: 75 } as const;

// Pack-variant tile params — mirror SlotTile's first-open lane request so
// the pack art is already CDN-cached from the ready step (same resized URL).
export const PACK_REEL_IMG = { width: 421, height: 526 } as const;

export interface CardSlotTileProps {
  /** Real pool-item card image for this slot (null → blank tile). */
  imageUrl: string | null;
  /** Pedestal under THIS card — every tile carries its own. */
  pedestalSrc: string;
  /** `true` for the tile that lands in the centre after the spin. Stays
   *  opaque while the neighbours fade in the `cleared` phase. */
  isLanded: boolean;
  /** `spin` → every tile opaque; `cleared` → all but the landed tile
   *  fade out so only the pulled card remains. */
  mode: AnimMode;
  /** Lane index, surfaced as `data-ti` so the reel can locate the winning
   *  tile (by index) for the residual centring snap at landing. */
  index?: number;
  /** `'card'` (default) — pool-item card standing on its own pedestal.
   *  `'pack'` — the sealed pack art, full-tile and pedestal-free, matching
   *  the first-open ReadyStep lane. */
  variant?: 'card' | 'pack';
  /** `true` while the parent plays its post-landing glow reveal — every
   *  tile (notably the landed one) crossfades out into the glow plate,
   *  mirroring the first-open lane's `reveal` phase. */
  revealing?: boolean;
}

/**
 * One slot on the "Open Next Pack" shuffle reel — a full card+pedestal
 * mini-scene (same proportions as the winner scene, aspect 320/500). Each
 * card stands on its OWN pedestal, so the whole reel reads as a row of
 * cards on plinths sliding past. The centred tile is pixel-identical to
 * the winner scene, so the landing seats the card exactly where the
 * winner card sits.
 *
 * `loading="eager"` + the parent's preload keep every tile decoded before
 * the spin, so nothing pops in mid-slide. `ref` is forwarded so the reel
 * can measure tile-step off the centre tile.
 */
export const CardSlotTile = forwardRef<HTMLDivElement, CardSlotTileProps>(
  function CardSlotTile(
    {
      imageUrl,
      pedestalSrc,
      isLanded,
      mode,
      index,
      variant = 'card',
      revealing = false,
    },
    ref,
  ) {
    // Sleek has no pedestal (partner mockup); read before the pack early return.
    const isSleek = useIsSleek();
    const opacity =
      revealing ||
      mode === 'aborting' ||
      mode === 'errored' ||
      (mode === 'cleared' && !isLanded)
        ? 0
        : 1;
    const fadeMs = revealing ? REVEAL_FADE_MS : CLEAR_TRANSITION_MS;
    // Measure the card's real aspect from the SAME cached reel URL so the
    // rounded clip hugs its true edges — identical treatment to WinnerContent.
    // Skipped in the pack variant (no rounded clip there).
    const cardAspect = useImageAspect(
      variant === 'pack'
        ? null
        : compressedImageUrl({ url: imageUrl, ...REEL_IMG }),
    );

    if (variant === 'pack') {
      // Pack tile — the sealed pack art centred in the same lane box, no
      // pedestal and no corner clip, visually mirroring the first-open lane.
      return (
        <div
          ref={ref}
          data-ti={index}
          className="relative h-full flex-shrink-0"
          style={{
            aspectRatio: '320 / 500',
            opacity,
            transition: `opacity ${fadeMs}ms ease-out`,
          }}
        >
          <Image
            src={imageUrl}
            alt=""
            loading="eager"
            className="absolute inset-0"
            imgClassName="absolute inset-0 h-full w-full object-contain"
            {...PACK_REEL_IMG}
          />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        data-ti={index}
        className="relative h-full flex-shrink-0"
        style={{
          aspectRatio: '320 / 500',
          opacity,
          transition: `opacity ${fadeMs}ms ease-out`,
        }}
      >
        {/* Pedestal — BEHIND the card (z-0), bottom-centred. Narrower than the
            tile so it matches the winner scene's small plinth. */}
        {!isSleek && (
          <img
            src={pedestalSrc}
            alt=""
            aria-hidden
            draggable={false}
            className="pointer-events-none absolute bottom-0 left-1/2 z-0 max-w-none -translate-x-1/2 select-none"
            style={{ width: '82%' }}
          />
        )}
        {/* Card — IN FRONT (z-10), top-anchored, 84% height, aspect 232/367.
            `object-bottom` seats its base on the pedestal; a tiny fixed
            `translateY(-3px)` lifts it ≈3px clear of the plinth — must match
            WinnerContent so the reel→winner landing has no jump. Centring is
            inline (translateX) to dodge the Tailwind translate-class doubling
            quirk. */}
        <div
          className="absolute left-1/2 top-0 z-10"
          style={{
            height: cardHeight(isSleek),
            aspectRatio: '232 / 367',
            transform: 'translateX(-50%) translateY(-3px)',
            // Size container so CARD_CORNER_RADIUS's `cqw` scales the
            // rounded clip with the rendered card (see readyStep.constants).
            containerType: 'size',
          }}
        >
          {/* Card clipped to its real (object-contain) rect with rounded
              corners — IDENTICAL to WinnerContent so the reel→winner landing
              shows the same rounded card (trims the source PNG's corner
              triangles). Before the aspect is known, fill the box with the
              object-contain image; once measured, the wrapper shrinks to the
              card's exact rect (same pixels, now clipped) and object-cover
              fills it. */}
          <div
            className="absolute overflow-hidden"
            style={
              cardAspect != null
                ? {
                    ...containedRectStyle(cardAspect, CARD_BOX_ASPECT),
                    borderRadius: CARD_CORNER_RADIUS,
                  }
                : { inset: 0 }
            }
          >
            <Image
              src={imageUrl}
              alt=""
              loading="eager"
              className="absolute inset-0"
              imgClassName={`absolute inset-0 h-full w-full ${
                cardAspect != null
                  ? 'object-cover'
                  : 'object-contain object-bottom'
              }`}
              {...REEL_IMG}
            />
          </div>
        </div>
      </div>
    );
  },
);
